/**
 * Create Bet Modal - form for creating a new bet
 */

import { useState } from "preact/hooks";
import type { User } from "../api/types";

interface CreateBetModalProps {
  users: User[];
  onClose: () => void;
  onCreate: (data: CreateBetData) => Promise<void>;
}

export interface CreateBetData {
  description: string;
  resolutionCriteria: string;
  initialPool: number;
  subjectUserId?: string;
}

export function CreateBetModal({
  users,
  onClose,
  onCreate,
}: CreateBetModalProps) {
  const [description, setDescription] = useState("");
  const [resolutionCriteria, setResolutionCriteria] = useState("");
  const [initialPool, setInitialPool] = useState(10);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Parse @mentions from description
  const parseMention = (text: string): string | undefined => {
    const match = text.match(/@([a-zA-Z0-9_]+)/);
    if (match && match[1]) {
      const mentionedName = match[1].toLowerCase();
      const mentionedUser = users.find(
        (u) => u.displayName.toLowerCase() === mentionedName,
      );
      return mentionedUser?.id;
    }
    return undefined;
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!description.trim()) {
      setError("Description is required");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const subjectUserId = parseMention(description);
      await onCreate({
        description: description.trim(),
        resolutionCriteria: resolutionCriteria.trim(),
        initialPool,
        subjectUserId,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create bet");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div class="modal-overlay" onClick={onClose}>
      <div class="modal create-bet-modal" onClick={(e) => e.stopPropagation()}>
        <button class="modal-close" onClick={onClose}>
          &times;
        </button>

        <h3>New Bet</h3>

        <form onSubmit={handleSubmit}>
          <div class="form-group">
            <label for="bet-description">What's the bet?</label>
            <textarea
              id="bet-description"
              value={description}
              onInput={(e) => setDescription(e.currentTarget.value)}
              placeholder="e.g., Sam won't swear tonight"
              required
            />
          </div>

          <div class="form-group">
            <label for="resolution-criteria">
              Resolution criteria (optional)
            </label>
            <input
              type="text"
              id="resolution-criteria"
              value={resolutionCriteria}
              onInput={(e) => setResolutionCriteria(e.currentTarget.value)}
              placeholder="How will this be decided?"
            />
          </div>

          <div class="form-group">
            <label for="initial-pool">Initial wager ($)</label>
            <input
              type="number"
              id="initial-pool"
              value={initialPool}
              onInput={(e) => setInitialPool(Number(e.currentTarget.value))}
              min={1}
            />
            <p class="hint">Your initial wager on YES</p>
          </div>

          {error && <div class="error">{error}</div>}

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" class="btn btn-primary" disabled={submitting}>
              {submitting ? "Creating..." : "Create Bet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
